// src/components/Messages.tsx
import { useState, useEffect, useRef } from "react";
import { useTranslation } from 'react-i18next'; 
import { Send, Search, Phone, MoreVertical, Circle, ArrowLeft } from 'lucide-react';
import { useMessaging } from '../../../contexts/MessagingContext';
import { toast } from 'sonner';
import Cookies from 'js-cookie';
import { Seo } from "../../components/Seo";

const Skeleton = ({ className = "" }: { className?: string }) => (
  <div className={`animate-pulse bg-gray-200 rounded-lg ${className}`} />
);

function MessagesSkeleton() {
  return (
    <div className="p-4 md:p-6 h-[calc(100vh-80px)]">
      <div className="bg-white rounded-2xl border border-[#E5E7EB] h-full flex overflow-hidden">
        {/* Sidebar Skeleton */}
        <div className="w-72 border-r border-[#E5E7EB] flex flex-col flex-shrink-0">
          <div className="p-4 border-b border-[#E5E7EB]">
            <Skeleton className="h-6 w-28 mb-4" />
            <Skeleton className="h-10 w-full rounded-xl" />
          </div>
          <div className="flex-1 overflow-hidden p-2">
            {[1, 2, 3, 4, 5].map((item) => (
              <div key={item} className="flex gap-3 items-center p-3 border-b border-[#F3F4F6]">
                <Skeleton className="w-10 h-10 rounded-xl" />
                <div className="flex-1">
                  <div className="flex justify-between mb-2">
                    <Skeleton className="h-4 w-24" />
                    <Skeleton className="h-3 w-8" />
                  </div>
                  <Skeleton className="h-3 w-28 mb-2" />
                  <Skeleton className="h-3 w-40" />
                </div>
                <Skeleton className="w-5 h-5 rounded-full" />
              </div>
            ))}
          </div>
        </div>
        {/* Chat Section Skeleton */}
        <div className="flex-1 flex flex-col">
          <div className="flex justify-between items-center p-4 border-b border-[#E5E7EB]">
            <div className="flex gap-3 items-center">
              <Skeleton className="w-10 h-10 rounded-xl" />
              <div><Skeleton className="h-4 w-28 mb-2" /><Skeleton className="h-3 w-40" /></div>
            </div>
            <div className="flex gap-2"><Skeleton className="w-9 h-9 rounded-xl" /><Skeleton className="w-9 h-9 rounded-xl" /></div>
          </div>
          <div className="flex-1 p-5 space-y-4 overflow-hidden">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className={`flex ${i % 2 === 0 ? 'justify-start' : 'justify-end'}`}>
                <Skeleton className={`h-${10 + i * 4} w-${60 + i * 10} rounded-2xl`} />
              </div>
            ))}
          </div>
          <div className="p-4 border-t border-[#E5E7EB]">
            <div className="flex gap-3"><Skeleton className="flex-1 h-11 rounded-xl" /><Skeleton className="w-11 h-11 rounded-xl" /></div>
          </div>
        </div>
      </div>
    </div>
  );
}

const getCurrentUser = () => {
  try {
    const userCookie = Cookies.get('user');

    if (!userCookie) {
      console.warn('⚠️ user cookie not found');
      return null;
    }

    return JSON.parse(userCookie);
  } catch (error) {
    console.error('❌ Invalid user cookie:', error);
    return null;
  }
};

export function Messages() {
  const { t } = useTranslation('auth'); 
  const {
    conversations,
    messages,
    sendMessage,
    loadConversations,
    loadMessages,
    markAsRead,
    unreadCounts,
  } = useMessaging();

  const [activeId, setActiveId] = useState<string | null>(null);
  const [newMsg, setNewMsg] = useState('');
  const [search, setSearch] = useState('');
  const [showChat, setShowChat] = useState(false);
  const [loading, setLoading] = useState(true);
  const bottomRef = useRef<HTMLDivElement>(null);
  const currentUser = getCurrentUser();
  const userId = currentUser?.id;

  // Load conversations on mount
  useEffect(() => {
    const init = async () => {
      await loadConversations();
      setLoading(false);
    };
    init();
  }, []);

  useEffect(() => {
    console.log('================================');
    console.log('👤 CURRENT USER');
    console.log('User:', currentUser);
    console.log('User ID:', userId);
    console.log('User Cookie:', Cookies.get('user'));
    console.log('Token Exists:', !!Cookies.get('token'));
    console.log('================================');
  }, [userId]);

  // When active conversation changes, load its messages
  useEffect(() => {
    if (activeId) {
      loadMessages(activeId);
      // Mark as read when opening
      markAsRead(activeId);
    }
  }, [activeId]);

  // Scroll to bottom when messages update
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages[activeId || '']]);

  // Filter conversations by search
  const filteredConversations = conversations.filter(conv => {
    const otherUser = conv.buyerId === userId ? conv.Seller : conv.Buyer;
    const name = otherUser?.FirstName ? `${otherUser.FirstName} ${otherUser.LastName || ''}`.trim() : '';
    const vehicle = conv.Vehicle?.Title || '';
    const searchLower = search.toLowerCase();
    return name.toLowerCase().includes(searchLower) || vehicle.toLowerCase().includes(searchLower);
  });

  // Get the active conversation data
  const activeConversation = conversations.find(c => c.id === activeId);
  const conversationMessages = activeId ? (messages[activeId] || []) : [];

  // Get display name for user
  const getDisplayName = (user: any) => {
    if (!user) return t('messages.chat.user');
    return user.FirstName ? `${user.FirstName} ${user.LastName || ''}`.trim() : t('messages.chat.user');
  };

  // Send message
  const handleSend = async () => {
    if (!newMsg.trim()) {
      return;
    }

    if (!activeId || !activeConversation) {
      return;
    }

    if (!userId) {
      console.error('❌ Current user ID is missing');
      toast.error(t('messages.errors.userNotFound'));
      return;
    }

    let receiverId: string;

    if (userId === activeConversation.buyerId) {
      receiverId = activeConversation.sellerId;
      console.log('👤 Current role: BUYER');
      console.log('🏪 Receiver: SELLER');
    } else if (userId === activeConversation.sellerId) {
      receiverId = activeConversation.buyerId;
      console.log('🏪 Current role: SELLER');
      console.log('👤 Receiver: BUYER');
    } else {
      console.error('❌ Current user does not belong to conversation');
      toast.error(t('messages.errors.notPartOfConversation'));
      return;
    }

    const messageText = newMsg.trim();

    console.log('==============================');
    console.log('📤 SEND MESSAGE');
    console.log('Current User ID:', userId);
    console.log('Conversation ID:', activeId);
    console.log('Vehicle ID:', activeConversation.vehicleId);
    console.log('Buyer ID:', activeConversation.buyerId);
    console.log('Seller ID:', activeConversation.sellerId);
    console.log('Receiver ID:', receiverId);
    console.log('Message:', messageText);
    console.log('==============================');

    try {
      const response = await sendMessage({
        vehicleId: activeConversation.vehicleId,
        receiverId,
        text: messageText,
      });

      console.log('✅ Message sent successfully:', response);

      setNewMsg('');
    } catch (err: any) {
      console.error('❌ Failed to send message:', err);
      toast.error(
        typeof err === 'string'
          ? err
          : err?.message || t('messages.errors.sendFailed')
      );
    }
  };

  if (loading) {
    return <MessagesSkeleton />;
  }

  // If no conversations, show empty state
  if (conversations.length === 0) {
    return (
      <div className="flex items-center justify-center h-full p-4">
        <div className="text-center">
          <div className="w-16 h-16 bg-[#F5F7FA] rounded-full flex items-center justify-center mx-auto mb-4">
            <Circle className="w-8 h-8 text-[#BFC2C7]" />
          </div>
          <p className="text-[#2D323A] text-lg font-medium">{t('messages.noConversations.title')}</p>
          <p className="text-[#717182] text-sm">{t('messages.noConversations.description')}</p>
        </div>
      </div>
    );
  }

  return (
    <>
    <Seo
      title="Messages | Morrocars"
      description="View and manage your conversations with buyers and sellers on Morrocars. Stay connected and respond to vehicle enquiries."
    />
    <div className="p-2 sm:p-4 md:p-6 h-[calc(100vh-80px)]">
      <div className="bg-white rounded-2xl border border-[#E5E7EB] h-full flex overflow-hidden min-w-0">
        {/* Sidebar */}
        <div className={`w-full md:w-72 border-r-0 md:border-r border-[#E5E7EB] flex-col flex-shrink-0 ${showChat ? 'hidden md:flex' : 'flex'}`}>
          <div className="p-4 border-b border-[#E5E7EB]">
            <h2 className="text-[#2D323A] mb-3">{t('messages.title')}</h2>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#717182]" />
              <input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder={t('searchPlaceholder')}
                className="w-full pl-10 pr-4 py-2 bg-[#F5F7FA] border border-[#E5E7EB] rounded-xl text-sm focus:outline-none focus:border-[#1E6DFF]"
              />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto">
            {filteredConversations.map(conv => {
              const otherUser = conv.buyerId === userId ? conv.Seller : conv.Buyer;
              const name = getDisplayName(otherUser);
              const avatar = otherUser?.FirstName?.charAt(0) || 'U';
              const lastMsg = conv.Messages?.[0]?.text || 'No messages yet';
              const time = conv.lastMessageAt ? new Date(conv.lastMessageAt).toLocaleString() : '';
              const unread = unreadCounts[conv.id] || 0;

              return (
                <button
                  key={conv.id}
                  onClick={() => { setActiveId(conv.id); setShowChat(true); }}
                  className={`w-full flex items-start gap-3 p-4 text-left transition-colors border-b border-[#E5E7EB] ${activeId === conv.id ? 'bg-[#1E6DFF]/5 border-l-2 border-l-[#1E6DFF]' : 'hover:bg-[#F5F7FA]'}`}
                >
                  <div className="relative flex-shrink-0">
                    <div className="w-10 h-10 bg-[#1E6DFF]/10 rounded-xl flex items-center justify-center text-[#1E6DFF]">
                      {avatar}
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-[#2D323A]">{name}</span>
                      <span className="text-xs text-[#717182]">{time}</span>
                    </div>
                    <p className="text-xs text-[#1E6DFF] truncate">{conv.Vehicle?.Title || 'Vehicle'}</p>
                    <p className="text-xs text-[#717182] truncate">{lastMsg}</p>
                  </div>
                  {unread > 0 && (
                    <span className="w-5 h-5 bg-[#1E6DFF] text-white text-xs rounded-full flex items-center justify-center flex-shrink-0">
                      {unread}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Chat Window */}
        {activeConversation ? (
          <div className={`flex-1 min-w-0 flex-col ${showChat ? 'flex' : 'hidden md:flex'}`}>
            {/* Chat Header */}
            <div className="flex items-center justify-between p-3 sm:p-4 border-b border-[#E5E7EB]">
              <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                <button
                  onClick={() => setShowChat(false)}
                  className="md:hidden p-2 -ml-1 rounded-xl hover:bg-[#F5F7FA] text-[#2D323A] flex-shrink-0"
                >
                  <ArrowLeft className="w-5 h-5" />
                </button>
                <div className="relative flex-shrink-0">
                  <div className="w-9 h-9 sm:w-10 sm:h-10 bg-[#1E6DFF]/10 rounded-xl flex items-center justify-center text-[#1E6DFF]">
                    {(() => {
                      const other = activeConversation.buyerId === userId ? activeConversation.Seller : activeConversation.Buyer;
                      return other?.FirstName?.charAt(0) || 'U';
                    })()}
                  </div>
                </div>
                <div className="min-w-0">
                  <p className="text-sm text-[#2D323A] truncate">
                    {(() => {
                      const other = activeConversation.buyerId === userId ? activeConversation.Seller : activeConversation.Buyer;
                      return getDisplayName(other);
                    })()}
                  </p>
                  <p className="text-xs text-[#717182] truncate">{activeConversation.Vehicle?.Title || 'Vehicle'}</p>
                </div>
              </div>
              <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
                <button className="p-2 hover:bg-[#F5F7FA] rounded-xl text-[#717182]">
                  <Phone className="w-4 h-4" />
                </button>
                <button className="p-2 hover:bg-[#F5F7FA] rounded-xl text-[#717182]">
                  <MoreVertical className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {conversationMessages.length === 0 && (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center">
                    <div className="w-12 h-12 bg-[#F5F7FA] rounded-full flex items-center justify-center mx-auto mb-2">
                      <Circle className="w-6 h-6 text-[#BFC2C7]" />
                    </div>
                    <p className="text-[#717182] text-sm">{t('messages.chat.noMessages')}</p>
                  </div>
                </div>
              )}
              {conversationMessages.map((msg) => {
                const isMe = String(msg.senderId) === String(userId);

                return (
                  <div
                    key={msg.id}
                    className={`flex w-full ${
                      isMe ? 'justify-end' : 'justify-start'
                    }`}
                  >
                    <div
                      className={`
                        max-w-[85%]
                        sm:max-w-[70%]
                        px-3
                        sm:px-4
                        py-2.5
                        rounded-2xl
                        text-sm
                        break-words
                        shadow-sm
                        ${
                          isMe
                            ? 'bg-[#1E6DFF] text-white rounded-br-sm'
                            : 'bg-[#F5F7FA] text-[#2D323A] rounded-bl-sm'
                        }
                      `}
                    >
                      <p>{msg.text}</p>

                      <div
                        className={`flex items-center justify-end gap-1 text-xs mt-1 ${
                          isMe
                            ? 'text-white/70'
                            : 'text-[#717182]'
                        }`}
                      >
                        <span>
                          {new Date(msg.createdAt).toLocaleTimeString([], {
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </span>

                        {isMe && (
                          <span>
                            {msg.isRead ? '✓✓' : '✓'}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
              <div ref={bottomRef} />
            </div>

            {/* Input */}
            <div className="p-2 sm:p-4 border-t border-[#E5E7EB]">
              <div className="flex gap-2 sm:gap-3">
                <input
                  value={newMsg}
                  onChange={e => setNewMsg(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleSend()}
                  placeholder={t('chat.typePlaceholder')}
                  className="flex-1 min-w-0 px-3 sm:px-4 py-2.5 bg-[#F5F7FA] border border-[#E5E7EB] rounded-xl focus:outline-none focus:border-[#1E6DFF] text-sm"
                />
                <button
                  onClick={handleSend}
                  className="flex-shrink-0 p-2.5 bg-[#1E6DFF] text-white rounded-xl hover:bg-[#1557d9] transition-colors"
                >
                  <Send className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex-1 flex items-center justify-center">
            <p className="text-[#717182]">{t('messages.chat.select')}</p>
          </div>
        )}
      </div>
    </div>
    </>
  );
}
